import React, { useState } from 'react';

import { TextareaAutosize, FormGroup, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Select from '../../../components/forms/Select';
import { StyledLabel } from '../../../components/forms/styles';
import { UnauthUserAccess } from '../../../types/adminConfig';

const CharacterCount = styled.div`
    color: ${(props) => (props.error ? props.theme.colors.error : 'inherit')};
    font-size: 12px;
    display: flex;
    align-items: center;
`;

const Label = styled.div`
    margin-right: 5px;
`;

const Value = styled.div``;

const StyledTextarea = styled(TextareaAutosize)`
    border: 1px solid ${(props) => (props.error ? props.theme.colors.error : props.theme.colors.border)};
    border-radius: 4px;
    padding: 0.5rem 0.75rem;
    font-family: Lato, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
        'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    font-size: 16px;
    max-width: 400px;
`;

const StyledFormGroupContainer = styled.div`
    margin-bottom: 1rem;
`;

const StyledSelect = styled(Select)`
    max-width: 400px;
`;

const CHAR_LIMIT = 250;

interface UnauthorizedUserAccessViewProps {
    setting: UnauthUserAccess;
    setSetting: (setting: UnauthUserAccess) => void;
    text: string | null;
    setText: (text: string) => void;
}

const UnauthorizedUserAccessView: React.FC<UnauthorizedUserAccessViewProps> = ({
    setting,
    setSetting,
    text,
    setText
}) => {
    const { t } = useTranslation('admin_settings');
    const [textareaVisible, setTextareaVisible] = useState(setting !== UnauthUserAccess.Allow);
    const [unauthText, setUnauthText] = useState(text || '');
    const [error, setError] = useState(false);

    const handleAccessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUnauthText('');

        if (event.target.value !== UnauthUserAccess.Allow) {
            setTextareaVisible(true);
        } else {
            setTextareaVisible(false);
        }

        setSetting(event.target.value as UnauthUserAccess);
    };

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUnauthText(event.target.value);
        const chars = event.target.value.length;

        if (chars <= CHAR_LIMIT) {
            setError(false);
        }

        if (chars > CHAR_LIMIT) {
            setError(true);
        }

        setText(event.target.value.substring(0, Math.min(CHAR_LIMIT, chars)));
    };

    const renderCharacterCount = () => {
        if (error) {
            return <CharacterCount error>{t('too-many-chars')}</CharacterCount>;
        }

        return (
            <CharacterCount error={error}>
                <Label>{t('characters-left')}</Label>
                <Value>{Math.max(CHAR_LIMIT - unauthText.length, 0)}</Value>
            </CharacterCount>
        );
    };

    return (
        <>
            <StyledFormGroupContainer>
                <FormGroup>
                    <StyledSelect
                        id="unauthUserAccess"
                        name="unauthUserAccess"
                        defaultValue={setting}
                        label={t('when-unauthed-users-join')}
                        onChange={handleAccessChange}
                    >
                        <MenuItem value={'allow'}>{t('allow-unauthed')}</MenuItem>
                        <MenuItem value={'signin'}>{t('signin-unauthed')}</MenuItem>
                        <MenuItem value={'deny'}>{t('deny-unauthed')}</MenuItem>
                    </StyledSelect>
                </FormGroup>
            </StyledFormGroupContainer>
            {textareaVisible && (
                <StyledFormGroupContainer>
                    <FormGroup>
                        <StyledLabel>{t('text-unauthed-label')}</StyledLabel>
                        <StyledTextarea
                            id="unauthUserText"
                            name="unauthUserText"
                            value={unauthText}
                            minRows={3}
                            maxRows={3}
                            onChange={handleTextChange}
                        ></StyledTextarea>
                        {renderCharacterCount()}
                    </FormGroup>
                </StyledFormGroupContainer>
            )}
        </>
    );
};

export default UnauthorizedUserAccessView;
